import { getState } from '/bb/state.js';
import {
  SECTION_VISIBLE_RATIO,
  SECTION_VISITS_CAP,
  SELECTION_DEBOUNCE_MS,
  SECTION_PAUSE_MS,
  READING_ACTIVITY_GAP_MS,
  BB_ATTRIBUTE,
  ATTR_SECTION,
} from '/bb/constants.js';
import { resolveSection } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateSection, elementContains } from '/bb/helpers.js';

let observer = null;
let mutationObserver = null;
const observedSections = new Map();
let pauseInterval = null;

function trackSection(element) {
  const section = resolveSection(element);
  if (!section) return;
  if (observedSections.has(element)) {
    observedSections.set(element, section);
    return;
  }
  observedSections.set(element, section);
  if (observer) observer.observe(element);
}

function untrackSection(element) {
  if (!observedSections.has(element)) return;
  observedSections.delete(element);
  if (observer) observer.unobserve(element);
}

function onIntersect(entries) {
  for (const entry of entries) {
    const section = observedSections.get(entry.target);
    if (!section) continue;
    const visible = entry.intersectionRatio >= SECTION_VISIBLE_RATIO;
    const pageKey = ensureCurrentPage();
    const sec = getOrCreateSection(pageKey, section.id, section.sectionCategories);
    captureTextLength(sec, entry.target);
    const t = Date.now();
    if (visible) startSectionVisibility(sec, t);
    else stopSectionVisibility(sec, t);
  }
}

function selectionDebounced() {
  if (typeof window === 'undefined') return function () {};
  let pending = false;
  let lastT = 0;
  return function () {
    const t = Date.now();
    if (pending) return;
    if (t - lastT < SELECTION_DEBOUNCE_MS) return;
    pending = true;
    setTimeout(() => {
      lastT = Date.now();
      pending = false;
      try {
        const sel = document.getSelection();
        if (!sel || sel.isCollapsed) return;
        const anchor = sel.anchorNode;
        if (!anchor) return;
        const anchorEl = anchor.nodeType === 1 ? anchor : anchor.parentElement;
        if (!anchorEl) return;
        for (const [el, section] of observedSections) {
          if (elementContains(el, anchorEl)) {
            const pageKey = ensureCurrentPage();
            const sec = getOrCreateSection(pageKey, section.id, section.sectionCategories);
            sec.selection_count += 1;
            sec.runtime.lastSelectionAt = lastT;
            sec.runtime.lastActivityAt = lastT;
            break;
          }
        }
      } catch (err) {
        /* ignore */
      }
    }, SELECTION_DEBOUNCE_MS);
  };
}

const selectionHandler = selectionDebounced();

function rescanSections() {
  if (typeof document === 'undefined') return;
  const els = document.querySelectorAll('[' + ATTR_SECTION + ']');
  for (const el of els) trackSection(el);
  for (const el of observedSections.keys()) {
    if (!document.contains(el)) untrackSection(el);
  }
}

export function syncVisibleSections(t = Date.now()) {
  if (!attached || typeof document === 'undefined') return;

  rescanSections();
  for (const [el, section] of observedSections) {
    if (visibleRatio(el) < SECTION_VISIBLE_RATIO) continue;
    const pageKey = ensureCurrentPage();
    const sec = getOrCreateSection(pageKey, section.id, section.sectionCategories);
    captureTextLength(sec, el);
    startSectionVisibility(sec, t);
  }
}

function captureTextLength(sec, element) {
  if (sec.text_length > 0 || !element) return;
  const text = typeof element.textContent === 'string' ? element.textContent.replace(/\s+/g, ' ').trim() : '';
  sec.text_length = text.length;
}

function startSectionVisibility(sec, t) {
  if (sec.runtime.currentlyVisible) return;

  sec.runtime.currentlyVisible = true;
  sec.runtime.enteredAt = t;
  sec.runtime.lastActivityAt = t;
  sec.runtime.pauseStartedAt = 0;
  sec.visits_count += 1;
  if (sec.visits.length < SECTION_VISITS_CAP) sec.visits.push(t);
}

function stopSectionVisibility(sec, t) {
  if (!sec.runtime.currentlyVisible) return;

  sec.runtime.currentlyVisible = false;
  sec.time_total_ms += Math.max(0, t - sec.runtime.enteredAt);
  if (sec.runtime.lastActivityAt > 0) {
    sec.reading_time_ms += Math.max(0, sec.runtime.lastActivityAt - sec.runtime.enteredAt);
  }
  sec.runtime.enteredAt = 0;
  sec.runtime.pauseStartedAt = 0;
}

function visibleRatio(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return 0;
  const rect = el.getBoundingClientRect();
  const area = Math.max(0, rect.width) * Math.max(0, rect.height);
  if (area <= 0) return 0;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const left = Math.max(0, rect.left);
  const top = Math.max(0, rect.top);
  const right = Math.min(viewportWidth, rect.right);
  const bottom = Math.min(viewportHeight, rect.bottom);
  const visibleWidth = Math.max(0, right - left);
  const visibleHeight = Math.max(0, bottom - top);
  return (visibleWidth * visibleHeight) / area;
}

function pauseTick() {
  const s = getState();
  const tNow = Date.now();
  for (const sec of s.sections.values()) {
    if (!sec.runtime.currentlyVisible) continue;
    const inactiveFor = tNow - (sec.runtime.lastActivityAt || sec.runtime.enteredAt);
    if (inactiveFor >= SECTION_PAUSE_MS && sec.runtime.pauseStartedAt === 0) {
      sec.runtime.pauseStartedAt = tNow;
      sec.pause_count += 1;
    } else if (inactiveFor < READING_ACTIVITY_GAP_MS && sec.runtime.pauseStartedAt !== 0) {
      sec.runtime.pauseStartedAt = 0;
    }
  }
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return;
  observer = new IntersectionObserver(onIntersect, {
    threshold: [0, SECTION_VISIBLE_RATIO, 1],
  });
  if (typeof MutationObserver !== 'undefined') {
    mutationObserver = new MutationObserver(rescanSections);
    mutationObserver.observe(document.body || document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [ATTR_SECTION, BB_ATTRIBUTE.SECTION_CATEGORY],
    });
  }
  document.addEventListener('selectionchange', selectionHandler, { passive: true });
  rescanSections();
  pauseInterval = setInterval(pauseTick, 500);
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  observedSections.clear();
  if (pauseInterval !== null) {
    clearInterval(pauseInterval);
    pauseInterval = null;
  }
  document.removeEventListener('selectionchange', selectionHandler);
  attached = false;
}
